import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Observable } from 'rxjs';
import {
  DidProvider,
  PbxDid,
  ReplaceWithExistingDidRequest,
  FiltersEmitModel,
  OrderDidRequest,
  AddTestingDidRequest,
  DidByCompanyIdResponse,
  AddDidRequest,
  DidsByCompanyIds,
  DID,
  UpdateProviderRequest,
} from 'voxloud-types/dist/models';

import { ErrorToken, createCustomErrorContext } from 'src/app/core/interceptors/generic-error-handling-interceptors';
import { Paginated } from 'src/app/shared/paginated.model';
import { AddDidManuallyRequest, OrderDidManuallyRequest, OrderDidManuallyResponse, PortabilityRequestDto } from '../models/dids.model';

@Injectable()
export class DidsService {
  private formValue;
  constructor(private http: HttpClient) { }

  getProviders(): Observable<DidProvider[]> {
    return this.http.get<DidProvider[]>(`${environment.apiEndpoint}/api/v1/providers`);
  }

  updateDidProvider(payload: UpdateProviderRequest, didId: number): Observable<string>{
    return this.http.put(
      `${environment.apiEndpoint}/api/v1/dids/${didId}`,
      payload,
      { responseType: 'text' }
    )
  }

  getDidsByPbx(pbxId: number): Observable<PbxDid[]> {
    return this.http.get<PbxDid[]>(`${environment.apiEndpoint}/api/v1/pbxes/${pbxId}/dids`);

  }

  checkPhoneNumberExists(phone: string) {
    return this.http.post(
      `${environment.apiEndpoint}/api/v2/onboarding/check/phone`,
      { phone: phone },
      { observe: "response" }
    );
  }

  // methods for new dids section
  getDids(idPage: any, size: any, filters?: FiltersEmitModel): Observable<Paginated<DID>> {
    let params = new HttpParams();
    if (filters) {
      for (let key of Object.keys(filters)) {
        params = params.append(key, filters[key]);
      }
    }

    params = params.append('page', idPage).append('size', size);
    return this.http.get<Paginated<DID>>(`${environment.apiEndpoint}/api/v1/dids`, { params });
  }

  getDidsByCompanyId(companyId: number): Observable<DidByCompanyIdResponse[]> {
    return this.http.get<DidByCompanyIdResponse[]>(`${environment.apiEndpoint}/api/v1/dids/by-company-ids?ids=${companyId}`);
  }

  getDid(number: string) {
    return this.http.get(`${environment.apiEndpoint}/api/v1/dids/by-number/${number}`);
  }

  getDidsByCompanyIds(companyId: number): Observable<DidsByCompanyIds[]> {
    return this.http.get<DidsByCompanyIds[]>(`${environment.apiEndpoint}/api/v1/dids/by-company-ids?ids=${companyId}`);
  }

  deleteDid(companyId: number, didId: number, errorToken?: ErrorToken[]) {
    return this.http.delete(
      `${environment.apiEndpoint}/api/v1/store/${companyId}/dids/${didId}`,
      errorToken ? { context: createCustomErrorContext(errorToken) } : undefined
    );
  }

  getPrefixesByCountry(country: string) {
    return this.http.get(`${environment.apiEndpoint}/api/v1/coverage/countries/${country}/areas`);
  }

  replaceWithExistingDid(companyId: number, didId: number, payload: ReplaceWithExistingDidRequest): Observable<string> {
    return this.http.put(
      `${environment.apiEndpoint}/api/v1/store/${companyId}/dids/${didId}/replace`,
      payload,
      { responseType: 'text' }
    )
  }

  orderDid(companyId: number, payload: OrderDidRequest): Observable<string> {
    return this.http.post(
      `${environment.apiEndpoint}/api/v1/store/${companyId}/dids/order`,
      payload,
      { responseType: 'text' }
    )
  }

  addExistingOrPortedDid(companyId: number, payload: AddDidRequest): Observable<string> {
    return this.http.post(
      `${environment.apiEndpoint}/api/v1/store/${companyId}/dids`,
      payload,
      { responseType: 'text' }
    );
  }

  activateDid(companyId: number, didId: number, errorToken?: ErrorToken[]): Observable<string> {
    return this.http.put(
      `${environment.apiEndpoint}/api/v1/store/${companyId}/dids/${didId}/activate`,
      null,
      errorToken ? { context: createCustomErrorContext(errorToken), responseType: 'text' } : { responseType: 'text' }
    );
  }

  activatePortedDid(companyId: number, didId: number, provider: string): Observable<string> {
    return this.http.put(
      `${environment.apiEndpoint}/api/v1/store/${companyId}/dids/${didId}/activate-ported?providerName=${provider}`,
      null,
      { responseType: 'text' }
    );
  }

  reactivateDeletedDid(companyId: number, didId: number): Observable<string> {
    return this.http.put(
      `${environment.apiEndpoint}/api/v1/store/${companyId}/dids/${didId}/reactivate`,
      null,
      { responseType: 'text' }
    );
  }

  addTestingDid(companyId: number, payload: AddTestingDidRequest) {
    return this.http.post(
      `${environment.apiEndpoint}/api/v1/store/${companyId}/dids`,
      payload,
      { responseType: 'text' }
    );
  }

  getPortabilityDocuments(companyId: number): Observable<PortabilityRequestDto[]> {
    return this.http.get<PortabilityRequestDto[]>(`${environment.apiEndpoint}/api/v1/portabilities/requests/by-company-id/${companyId}`);
  }

  getPortabilityDocumentsById(id: number): Observable<PortabilityRequestDto> {
    return this.http.get<PortabilityRequestDto>(`${environment.apiEndpoint}/api/v1/portabilities/requests/${id}`);
  }

  addTemporaryDid(companyId: number): Observable<DID> {
    return this.http.post<DID>(`${environment.apiEndpoint}/api/v1/dids/pool/assign`, {
      company_id: companyId
    });
  }

  // Methods to manage DID manually
  public orderDidManually(payload: OrderDidManuallyRequest): Observable<OrderDidManuallyResponse> {
    return this.http.post<OrderDidManuallyResponse>(`${environment.apiEndpoint}/api/v1/did-orders`, payload);
  }

  public addDidManually(payload: AddDidManuallyRequest): Observable<DID> {
    return this.http.post<DID>(`${environment.apiEndpoint}/api/v1/dids`, payload);
  }

  public activateDidManually(didId: number, errorToken?: ErrorToken[]): Observable<DID> {
    return this.http.put<DID>(
      `${environment.apiEndpoint}/api/v1/dids/${didId}/activate`,
      null,
      errorToken ? { context: createCustomErrorContext(errorToken) } : undefined
    );
  }

  public reactivateDeletedDidManually(didId: number): Observable<DID> {
    return this.http.put<DID>(`${environment.apiEndpoint}/api/v1/dids/${didId}/reactivate`, null);
  }

  public deleteDidManually(didId: number, errorToken?: ErrorToken[]) {
    return this.http.delete(
      `${environment.apiEndpoint}/api/v1/dids/${didId}`,
      errorToken ? { context: createCustomErrorContext(errorToken) } : undefined
    );
  }

  public toggleWhatsapp(id: number): Observable<DID> {
    return this.http.put<DID>(`${environment.apiEndpoint}/api/v1/dids/${id}/toggle-whatsapp-enabled`, null);
  }

  public toggleInboundSms(id: number): Observable<DID> {
    return this.http.put<DID>(`${environment.apiEndpoint}/api/v1/dids/${id}/toggle-inbound-sms-enabled`, null);
  }

  // Helper methods
  getAddPortDidFormValue() {
    return this.formValue
  }

  setAddPortDidFormValue(value) {
    this.formValue = value;
  }

}
